import { createContext, useContext, useState } from 'react';

const translations = {
  fr: {
    nav: {
      home: 'Accueil',
      formation: 'Construire ma formation',
      scenarios: 'Nos scénarios',
      demo: 'Plateforme de formation',
      about: 'À propos',
      contact: 'Contact',
      cta: "Demande d'informations",
    },
    home: {
      heroTitle: 'Anticiper pour se protéger. Décider sous incertitude. Apprendre par le jeu.',
      heroP1: 'Conçue par des professionnels du renseignement et de la planification opérationnelle des armées, la plateforme',
      heroP1Bold: 'Anticiper',
      heroP1End: 'est une application SaaS de formation au traitement de l\'information en vue de la décision.',
      heroP2Start: 'Elle permet de créer des',
      heroP2Bold: 'serious games immersifs',
      heroP2End: 'pour apprendre à se protéger des actions malveillantes et à anticiper le développement des crises.',
      heroP3Start: 'L\'équipe Anticiper propose plusieurs scénarios de jeux (crise réputationnelle, anticipation stratégique, détection de signaux faibles, tensions régionales entre des acteurs hostiles), ou crée des',
      heroP3Bold: 'scénarios sur mesure',
      heroP3End: 'adaptés à vos enjeux opérationnels.',
      heroCta: 'Construire ma formation',
      heroCtaSecondary: "Demande d'informations",
      gameTitle: 'Comment se déroule un serious game ?',
      gameP1: 'Les participants incarnent le rôle d\'un des membres d\'un CODIR d\'une entreprise, d\'une ONG fictive ou d\'un centre de suivi des crises de niveau gouvernemental. Chaque rôle est joué par un binôme. Chaque membre de l\'équipe reçoit des informations par mail ou par téléphone en provenance de son entourage professionnel ou personnel.',
      gameP2: 'Au début du jeu, les participants reçoivent une mission : structurer un plan d\'action pour se protéger, analyser des signaux faibles précurseurs d\'une action malveillante, ou structurer une réponse à une attaque informationnelle.',
      gameBadge1: 'Interaction simulée avec l\'entourage',
      gameBadge2: 'Informations différentes en fonction des rôles',
      saasIntro: 'Pour réaliser un serious game, les participants se connectent à l\'application de formation Anticiper.',
      saasTitle: 'Une plateforme SaaS aux caractéristiques uniques',
      saasItems: [
        { title: 'Bout en bout', desc: 'De la commande initiale au livrable final, sans rupture de flux' },
        { title: 'Écosystème intégré', desc: 'Orientations, interactions avec les capteurs et l\'environnement externe simulés en temps réel' },
        { title: 'Rôles différenciés', desc: 'Informations filtrées et adaptées selon le rôle de chaque joueur dans la chaîne de renseignement' },
        { title: 'Données fictives réalistes', desc: 'Des jeux de données représentatifs mais entièrement fictifs, garantissant sécurité et immersion' },
        { title: 'Application web intuitive', desc: 'Accessible depuis un navigateur, sans installation, pour une prise en main immédiate' },
      ],
      boardIntro: 'Nous utilisons également comme support pédagogique deux jeux de plateau créés par Anticiper en partenariat avec',
      boardMatagot: 'Matagot',
      humintTitle: 'Opération HUMINT',
      humintP1: 'Services Spéciaux - Opération HUMINT est un jeu de société compétitif et de négociation dont les mécanismes s\'inspirent des différentes étapes d\'une opération de renseignement humain.',
      humintP2: 'Jouez le rôle d\'un agent de la DGSE, de la CIA, du SVR russe ou encore du Guoanbu chinois agissant sous couverture diplomatique dans une grande capitale !',
      humintP3: 'Votre mission :',
      humintP3End: 'Collecter des informations secrètes pour protéger les intérêts vitaux de votre pays. Pour gagner, constituez avant les autres services un réseau clandestin de sources humaines !',
      shadowTitle: 'Shadow Corps',
      shadowP1: 'Les joueurs incarnent une société privée de renseignement opérant secrètement contre une entreprise-cible fictive. Leur objectif : réussir des actions d\'ingérence tout en évitant d\'être détectés.',
      shadowP2Start: 'Nous plaçons les joueurs dans le rôle de l\'attaquant pour susciter plus d\'adhésion et d\'engagement cognitif. Shadow Corps est un',
      shadowP2Bold: 'jeu collaboratif',
      shadowP2End: 'qui renforce les interactions interpersonnelles et l\'élaboration d\'une stratégie en équipe.',
      quizTitle: 'Construisez votre session de formation Anticiper',
      quizSubtitle: 'Répondez à quelques questions pour obtenir une estimation personnalisée.',
      timelineTitle: 'Les étapes d\'une session Anticiper',
      timeline: [
        { label: 'En amont', desc: 'Choix d\'un scénario en fonction des compétences techniques que vous souhaitez acquérir' },
        { label: 'J-7', desc: 'Réception d\'un dossier de mise dans l\'ambiance et attribution des rôles de chacun' },
        { label: 'J-5', desc: 'Visio de présentation du jeu et de l\'application' },
        { label: 'Jour J', desc: 'Déroulement du jeu' },
        { label: 'J+1', desc: 'Envoi d\'une attestation de participation et d\'un document récapitulatif des connaissances acquises' },
      ],
      skillsTitle: 'Nos serious games permettent de développer 7 compétences clés',
      skills: [
        'Identifier les signaux faibles précurseurs de risques, menaces ou opportunités',
        'Prendre des décisions sous contrainte de temps avec un haut degré d\'incertitude',
        'Stimuler la vigilance, le discernement et le partage d\'information inter-fonctions',
        'Renforcer la cohésion d\'une équipe en situation de surcharge informationnelle',
        'Fiabiliser une information',
        'Maîtriser les biais cognitifs',
        'Valoriser vos entretiens professionnels et vos compétences de négociation',
      ],
      ctaTitle: 'Prêt à renforcer la résilience de votre organisation ?',
      ctaSubtitle: 'Contactez-nous pour découvrir comment Anticiper peut transformer la préparation de vos équipes face aux crises.',
      ctaButton: "Demande d'informations",
    },
    scenarios: {
      title: 'Nos scénarios',
      subtitle: 'Chaque scénario est conçu pour développer des compétences spécifiques, dans un cadre immersif adapté à vos enjeux opérationnels.',
      objectives: 'Objectifs',
      theme: 'Thème',
      customCta: 'Nous créons également des scénarios sur mesure adaptés à vos enjeux opérationnels.',
      ctaButton: "Demande d'informations",
      items: [
        { name: 'Secure Trail', participants: '5 à 12 participants', duration: '1/2 journée', objectives: ['Détecter les signaux faibles précurseurs d\'une ingérence', 'Prendre des décisions sous forte incertitude et contrainte de temps', 'Renforcer la coopération inter-fonctions et la cohésion d\'équipe'], theme: 'Les participants incarnent les membres de l\'équipe dirigeante d\'une PME développant une technologie de rupture. Son développement rapide a suscité l\'intérêt d\'acteurs sensibles mais aussi l\'attention d\'États concurrents, de fonds d\'investissement prédateurs et d\'acteurs malveillants. Ils devront mettre en place un plan d\'action pour se protéger.', video: { eyebrow: 'Découvrir le scénario', title: 'Secure Trail en vidéo', description: 'Plongez dans le scénario Secure Trail et découvrez comment les participants apprennent à détecter les signaux faibles et à protéger leur organisation.', playLabel: 'Lire la vidéo', ariaLabel: 'Présentation vidéo du scénario Secure Trail', fallback: 'Votre navigateur ne supporte pas la lecture vidéo HTML5.' } },
        { name: 'Puno', participants: '5 à 12 participants', duration: '1 journée', objectives: ['Produire en équipe des recommandations stratégiques à destination de l\'échelon décisionnaire', 'Produire des éléments de langage et leurs vecteurs de diffusion, face à une attaque informationnelle', 'Identifier et limiter l\'impact des biais cognitifs sur l\'analyse collective de la situation'], theme: 'Vous appartenez à une ONG active au Pérou, spécialisée dans l\'extraction aurifère éthique. Les informations que vous parviendrez à collecter en équipe, auprès d\'un réseau de personnes de confiance, seront essentielles pour justifier le maintien de votre mission.' },
        { name: 'Crise réputationnelle', participants: '5 à 12 participants', duration: '1/2 journée (à la suite de Puno)', objectives: ['Savoir identifier les signaux faibles précurseurs de la crise (fuites dans la presse, pressions politiques, réactions de partenaires, etc.)'], theme: 'Un module complémentaire conçu pour être joué à la suite de l\'exercice Puno, centré sur la gestion d\'une crise réputationnelle et la détection de ses signaux avant-coureurs.' },
      ],
    },
    formation: {
      title: 'Construire ma formation',
      subtitle: 'Répondez à quelques questions pour identifier les formats de formation les plus adaptés à vos besoins.',
    },
    quiz: {
      questions: [
        { question: 'Quel savoir-faire technique souhaitez-vous travailler ?', subtitle: null, options: ['Protection et sécurité', 'Anticipation de crise réputationnelle', 'Analyse du renseignement', 'Elaboration de recommandations stratégiques pour l\'échelon décisionnaire'] },
        { question: 'Souhaitez-vous utiliser comme support pédagogique un serious game sous format numérique ou un jeu de plateau ?', subtitle: null, options: ['Serious game numérique', 'Jeu de plateau', 'Les deux'] },
        { question: 'Quelles sont les 3 compétences que vous souhaitez plus particulièrement travailler ?', subtitle: 'Sélectionnez 3 compétences maximum', options: ['Identifier les signaux faibles précurseurs de risques, menaces ou opportunités', 'Prendre des décisions sous contrainte de temps avec un haut degré d\'incertitude', 'Stimuler la vigilance, le discernement et le partage d\'information inter-fonctions', 'Renforcer la cohésion d\'une équipe en situation de surcharge informationnelle', 'Fiabiliser une information', 'Maîtriser les biais cognitifs', 'Valoriser vos entretiens professionnels et vos compétences de négociation'] },
        { question: 'Combien de personnes souhaitez-vous former par session ?', subtitle: null, options: ['Moins de 10 personnes', 'Entre 10 et 20 personnes'] },
        { question: 'De combien de temps disposez-vous ?', subtitle: null, options: ['Une demi-journée', 'Une journée', 'Entre deux et quatre jours'] },
        { question: 'Combien de temps souhaitez-vous consacrer à l\'apport théorique ?', subtitle: null, options: ['Une heure', 'Une demi-journée', 'Plus'] },
        { question: 'Où se déroulera la formation ?', subtitle: null, placeholder: 'Ex : Paris, dans vos locaux, en distanciel...' },
      ],
      resultTitle: 'Nos recommandations pour votre session',
      resultSubtitle: 'Sur la base de vos réponses, voici les formats que nous vous recommandons.',
      modifyAnswers: 'Modifier mes réponses',
      viewScenarios: 'Voir tous les scénarios',
      ctaButton: "Demande d'informations",
      seriousGame: 'Serious game',
      boardGame: 'Jeu de plateau',
      next: 'Suivant',
      previous: 'Précédent',
      seeEstimate: 'Voir les recommandations',
    },
    hero: {
      overline: 'PLATEFORME DE FORMATION',
      title: 'Anticipez les crises\navant qu\'elles ne surviennent',
      subtitle: 'Formez vos équipes à détecter les signaux faibles précurseurs d\'ingérences et à gérer les situations de crise grâce à des exercices immersifs et collaboratifs.',
      cta: "Demande d'informations",
      ctaSecondary: 'Découvrir la plateforme',
    },
    benefits: {
      overline: 'POURQUOI ANTICIPER',
      title: 'Une approche unique pour former à la détection des signaux faibles précurseurs d\'ingérence et à la gestion de crise',
      items: [
        {
          title: 'Scénarios immersifs',
          desc: 'Plongez vos équipes dans des situations réalistes de crise, d\'ingérence ou de déstabilisation, conçues par des experts.',
        },
        {
          title: 'Détection des signaux faibles',
          desc: 'Apprenez à identifier, trier et analyser les indices précurseurs d\'un risque, d\'une menace ou d\'une opportunité.',
        },
        {
          title: 'Livrables opérationnels',
          desc: 'Produisez des rapports, des notes de synthèse et des fiches d\'analyse directement exploitables en situation réelle.',
        },
        {
          title: 'Cartographie de votre écosystème',
          desc: 'Modélisez les acteurs, les flux et les interdépendances de votre environnement professionnel pour identifier les vulnérabilités et les leviers d\'influence en intelligence économique.',
        },
        {
          title: 'Chronologie dynamique',
          desc: 'Naviguez dans le temps pour comprendre l\'enchaînement des événements et anticiper les prochaines étapes d\'une crise.',
        },
        {
          title: 'Collaboration en temps réel',
          desc: 'Travaillez simultanément sur les mêmes documents, cartes et analyses grâce à notre technologie de synchronisation instantanée.',
        },
      ],
    },
    trust: {
      title: 'Conçue pour les professionnels exigeants',
      items: ['Exercices réalistes', 'Experts certifiés', 'Infrastructure souveraine', 'Données hébergées en France'],
    },
    finalCta: {
      title: 'Prêt à renforcer la résilience de votre organisation ?',
      subtitle: 'Contactez-nous pour découvrir comment Anticiper peut transformer la préparation de vos équipes face aux crises.',
      cta: "Demande d'informations",
    },
    demo: {
      overline: 'FONCTIONNALITÉS',
      title: 'Découvrez notre plateforme de formation',
      subtitle: 'Chaque module a été conçu pour reproduire les conditions réelles d\'une cellule de crise, de l\'analyse stratégique à la prise de décision.',
      features: [
        {
          tag: 'MESSAGERIE',
          title: 'Communiquez comme en situation réelle',
          desc: 'Une messagerie complète intégrée à l\'exercice : boîte de réception, envoi, dossiers, tags et contacts. Vos participants échangent des emails fictifs réalistes pour simuler les communications d\'une véritable cellule de crise.',
        },
        {
          tag: 'LIVRABLES & ANALYSE',
          title: 'Produisez des livrables structurés',
          desc: 'Rédigez vos analyses dans un éditeur collaboratif en temps réel. Les grilles d\'analyse prêtes à l\'emploi guident vos participants dans la structuration de leur raisonnement.',
        },
        {
          tag: 'CARTOGRAPHIE DE L\'ÉCOSYSTÈME',
          title: 'Visualisez les réseaux et les acteurs',
          desc: 'Cartographiez l\'environnement professionnel et les relations entre acteurs grâce à un outil de graphes interactif. Identifiez les connexions clés, les dépendances et les points de vulnérabilité.',
        },
        {
          tag: 'BASE DOCUMENTAIRE',
          title: 'Exploitez vos documents stratégiques',
          desc: 'Consultez et analysez vos documents de référence directement depuis la plateforme. Rapports de terrain, notes de renseignement, documents de contexte : chaque pièce est accessible par toute la cellule.',
        },
        {
          tag: 'INTELLIGENCE ARTIFICIELLE',
          title: 'Augmentez vos capacités d\'analyse avec l\'IA',
          desc: 'Un assistant IA interroge l\'ensemble de vos documents et communications pour vous aider à retrouver des informations clés, synthétiser des échanges et accélérer la prise de décision.',
        },
      ],
    },
    about: {
      overline: 'À PROPOS',
      title: 'Former pour mieux anticiper',
      intro: 'Anticiper est née d\'un constat simple : les organisations qui gèrent le mieux les crises sont celles qui s\'y sont préparées. Notre plateforme met à disposition des outils professionnels de simulation pour que chaque équipe puisse s\'entraîner dans des conditions proches du réel.',
      methodology: {
        title: 'Notre méthodologie',
        desc: 'Notre approche repose sur trois piliers fondamentaux, issus des meilleures pratiques de la formation opérationnelle et de la gestion de crise.',
        pillars: [
          { title: 'Immersion', desc: 'Des scénarios réalistes et évolutifs qui placent les participants en situation de décision sous pression.' },
          { title: 'Collaboration', desc: 'Un environnement de travail partagé qui reproduit les dynamiques d\'une véritable cellule de crise.' },
          { title: 'Analyse', desc: 'Des outils structurés pour développer l\'esprit critique et la capacité à détecter les signaux faibles.' },
        ],
      },
      expertise: {
        title: 'Notre expertise',
        desc: 'L\'équipe Anticiper réunit des spécialistes de la défense, de l\'intelligence économique et de la formation professionnelle. Notre expérience terrain garantit des exercices pertinents et immédiatement opérationnels.',
      },
    },
    contact: {
      overline: 'CONTACT',
      title: "Demande d'informations",
      subtitle: 'Vous souhaitez en savoir plus sur la plateforme Anticiper ? Remplissez le formulaire ci-dessous et notre équipe vous recontactera sous 24 heures.',
      trust: ['Réponse sous 24h', 'Démonstration personnalisée', 'Sans engagement'],
      form: {
        name: 'Nom complet',
        email: 'Email professionnel',
        company: 'Entreprise',
        phone: 'Téléphone (optionnel)',
        message: 'Votre message',
        messagePlaceholder: 'Décrivez votre besoin en quelques mots...',
        submit: 'Envoyer ma demande',
        success: 'Votre demande a bien été envoyée. Nous vous recontacterons rapidement.',
      },
    },
    footer: {
      tagline: 'Plateforme de formation à la gestion de crise et à la détection des signaux faibles.',
      nav: 'Navigation',
      legal: 'Mentions légales',
      privacy: 'Politique de confidentialité',
      copyright: '© 2026 Anticiper. Tous droits réservés.',
    },
  },
  en: {
    nav: {
      home: 'Home',
      formation: 'Build my training',
      scenarios: 'Our scenarios',
      demo: 'Training platform',
      about: 'About',
      contact: 'Contact',
      cta: 'Request information',
    },
    home: {
      heroTitle: 'Anticipate to protect. Decide under uncertainty. Learn through play.',
      heroP1: 'Designed by intelligence professionals and military operational planners, the',
      heroP1Bold: 'Anticiper',
      heroP1End: 'platform is a SaaS application for training in information processing for decision-making.',
      heroP2Start: 'It enables the creation of',
      heroP2Bold: 'immersive serious games',
      heroP2End: 'to learn how to protect against malicious actions and anticipate the development of crises.',
      heroP3Start: 'The Anticiper team offers several game scenarios (reputational crisis, strategic anticipation, weak signal detection, regional tensions between hostile actors), or creates',
      heroP3Bold: 'tailor-made scenarios',
      heroP3End: 'adapted to your operational challenges.',
      heroCta: 'Build my training',
      heroCtaSecondary: 'Request information',
      gameTitle: 'How does a serious game work?',
      gameP1: 'Participants play the role of a member of the executive committee of a company, a fictional NGO, or a government-level crisis monitoring center. Each role is played by a pair. Each team member receives information by email or phone from their professional or personal network.',
      gameP2: 'At the start of the game, participants receive a mission: structure an action plan for protection, analyze weak signals preceding a malicious action, or structure a response to an information attack.',
      gameBadge1: 'Simulated interaction with contacts',
      gameBadge2: 'Different information based on roles',
      saasIntro: 'To run a serious game, participants log into the Anticiper training application.',
      saasTitle: 'A SaaS platform with unique features',
      saasItems: [
        { title: 'End-to-end', desc: 'From the initial order to the final deliverable, without workflow interruption' },
        { title: 'Integrated ecosystem', desc: 'Orientations, interactions with sensors and the external environment simulated in real time' },
        { title: 'Differentiated roles', desc: 'Information filtered and adapted according to each player\'s role in the intelligence chain' },
        { title: 'Realistic fictional data', desc: 'Representative but entirely fictional datasets, ensuring security and immersion' },
        { title: 'Intuitive web application', desc: 'Accessible from a browser, no installation required, for immediate use' },
      ],
      boardIntro: 'We also use as educational support two board games created by Anticiper in partnership with',
      boardMatagot: 'Matagot',
      humintTitle: 'Operation HUMINT',
      humintP1: 'Special Services - Operation HUMINT is a competitive negotiation board game whose mechanics are inspired by the different stages of a human intelligence operation.',
      humintP2: 'Play the role of a DGSE, CIA, Russian SVR or Chinese Guoanbu agent operating under diplomatic cover in a major capital!',
      humintP3: 'Your mission:',
      humintP3End: 'Collect secret information to protect the vital interests of your country. To win, build a clandestine network of human sources before the other services!',
      shadowTitle: 'Shadow Corps',
      shadowP1: 'Players embody a private intelligence company operating secretly against a fictional target company. Their goal: carry out interference actions while avoiding detection.',
      shadowP2Start: 'We place players in the attacker\'s role to foster greater engagement and cognitive involvement. Shadow Corps is a',
      shadowP2Bold: 'collaborative game',
      shadowP2End: 'that strengthens interpersonal interactions and team strategy development.',
      quizTitle: 'Build your Anticiper training session',
      quizSubtitle: 'Answer a few questions to get a personalized recommendation.',
      timelineTitle: 'The stages of an Anticiper session',
      timeline: [
        { label: 'Upstream', desc: 'Choice of scenario based on the technical skills you want to develop' },
        { label: 'D-7', desc: 'Reception of a briefing pack and role assignments' },
        { label: 'D-5', desc: 'Video presentation of the game and the application' },
        { label: 'Day D', desc: 'Game session' },
        { label: 'D+1', desc: 'Sending of a participation certificate and a knowledge summary document' },
      ],
      skillsTitle: 'Our serious games develop 7 key skills',
      skills: [
        'Identify weak signals that precede risks, threats or opportunities',
        'Make decisions under time pressure with high uncertainty',
        'Stimulate vigilance, discernment and cross-functional information sharing',
        'Strengthen team cohesion under information overload',
        'Verify and validate information',
        'Master cognitive biases',
        'Enhance your professional interviews and negotiation skills',
      ],
      ctaTitle: 'Ready to strengthen your organization\'s resilience?',
      ctaSubtitle: 'Contact us to discover how Anticiper can transform your teams\' crisis preparedness.',
      ctaButton: 'Request information',
    },
    scenarios: {
      title: 'Our scenarios',
      subtitle: 'Each scenario is designed to develop specific skills, in an immersive framework adapted to your operational challenges.',
      objectives: 'Objectives',
      theme: 'Theme',
      customCta: 'We also create tailor-made scenarios adapted to your operational challenges.',
      ctaButton: 'Request information',
      items: [
        { name: 'Secure Trail', participants: '5 to 12 participants', duration: 'Half day', objectives: ['Detect weak signals preceding interference', 'Make decisions under high uncertainty and time pressure', 'Strengthen cross-functional cooperation and team cohesion'], theme: 'Participants play the members of the executive team of an SME developing a breakthrough technology. Its rapid growth has attracted the interest of sensitive actors as well as the attention of competing states, predatory investment funds, and malicious actors. They will need to implement an action plan to protect themselves.', video: { eyebrow: 'Discover the scenario', title: 'Secure Trail on video', description: 'Step into the Secure Trail scenario and see how participants learn to detect weak signals and protect their organization.', playLabel: 'Play video', ariaLabel: 'Video presentation of the Secure Trail scenario', fallback: 'Your browser does not support HTML5 video.' } },
        { name: 'Puno', participants: '5 to 12 participants', duration: '1 day', objectives: ['Produce strategic recommendations for decision-makers as a team', 'Produce key messages and their dissemination channels in response to an information attack', 'Identify and limit the impact of cognitive biases on collective situation analysis'], theme: 'You belong to an NGO active in Peru, specialized in ethical gold extraction. The information you manage to collect as a team, from a network of trusted contacts, will be essential to justify the continuation of your mission.' },
        { name: 'Reputational crisis', participants: '5 to 12 participants', duration: 'Half day (following Puno)', objectives: ['Know how to identify weak signals preceding a crisis (press leaks, political pressures, partner reactions, etc.)'], theme: 'A complementary module designed to be played after the Puno exercise, focused on managing a reputational crisis and detecting its early warning signs.' },
      ],
    },
    formation: {
      title: 'Build my training',
      subtitle: 'Answer a few questions to identify the training formats best suited to your needs.',
    },
    quiz: {
      questions: [
        { question: 'What technical skill do you want to work on?', subtitle: null, options: ['Security and protection', 'Reputational crisis anticipation', 'Intelligence analysis', 'Strategic recommendations for decision-makers'] },
        { question: 'Would you like to use a digital serious game or a board game as educational support?', subtitle: null, options: ['Digital serious game', 'Board game', 'Both'] },
        { question: 'Which 3 skills would you particularly like to develop?', subtitle: 'Select up to 3 skills', options: ['Identify weak signals preceding risks, threats or opportunities', 'Make decisions under time pressure with high uncertainty', 'Stimulate vigilance, discernment and cross-functional information sharing', 'Strengthen team cohesion under information overload', 'Verify and validate information', 'Master cognitive biases', 'Enhance your professional interviews and negotiation skills'] },
        { question: 'How many people do you want to train per session?', subtitle: null, options: ['Less than 10 people', 'Between 10 and 20 people'] },
        { question: 'How much time do you have?', subtitle: null, options: ['Half a day', 'One day', 'Between two and four days'] },
        { question: 'How much time do you want to dedicate to theory?', subtitle: null, options: ['One hour', 'Half a day', 'More'] },
        { question: 'Where will the training take place?', subtitle: null, placeholder: 'E.g.: Paris, at your offices, remotely...' },
      ],
      resultTitle: 'Our recommendations for your session',
      resultSubtitle: 'Based on your answers, here are the formats we recommend.',
      modifyAnswers: 'Modify my answers',
      viewScenarios: 'View all scenarios',
      ctaButton: 'Request information',
      seriousGame: 'Serious game',
      boardGame: 'Board game',
      next: 'Next',
      previous: 'Previous',
      seeEstimate: 'See recommendations',
    },
    hero: {
      overline: 'TRAINING PLATFORM',
      title: 'Anticipate crises\nbefore they happen',
      subtitle: 'Train your teams to detect weak signals that precede interference and manage crisis situations through immersive, collaborative exercises.',
      cta: 'Request information',
      ctaSecondary: 'Discover the platform',
    },
    benefits: {
      overline: 'WHY ANTICIPER',
      title: 'A unique approach to crisis management training',
      items: [
        {
          title: 'Immersive scenarios',
          desc: 'Immerse your teams in realistic crisis, interference and destabilization situations, designed by experts.',
        },
        {
          title: 'Weak signal detection',
          desc: 'Learn to identify, sort and analyze the precursor signs of a risk, a threat or an opportunity.',
        },
        {
          title: 'Operational deliverables',
          desc: 'Produce reports, briefing notes and analysis sheets directly usable in real situations.',
        },
        {
          title: 'Ecosystem mapping',
          desc: 'Model stakeholders, flows and interdependencies within your professional environment to identify vulnerabilities and leverage points in competitive intelligence.',
        },
        {
          title: 'Dynamic timeline',
          desc: 'Navigate through time to understand the sequence of events and anticipate the next stages of a crisis.',
        },
        {
          title: 'Real-time collaboration',
          desc: 'Work simultaneously on the same documents, maps and analyses thanks to our instant synchronization technology.',
        },
      ],
    },
    trust: {
      title: 'Designed for demanding professionals',
      items: ['Realistic exercises', 'Certified experts', 'Sovereign infrastructure', 'Data hosted in France'],
    },
    finalCta: {
      title: 'Ready to strengthen your organization\'s resilience?',
      subtitle: 'Contact us to discover how Anticiper can transform your teams\' crisis preparedness.',
      cta: 'Request information',
    },
    demo: {
      overline: 'FEATURES',
      title: 'Discover our training platform',
      subtitle: 'Each module has been designed to reproduce real crisis cell conditions, from strategic analysis to decision-making.',
      features: [
        {
          tag: 'MESSAGING',
          title: 'Communicate as in a real situation',
          desc: 'A complete messaging system integrated into the exercise: inbox, sending, folders, tags and contacts. Participants exchange realistic fictional emails to simulate real crisis cell communications.',
        },
        {
          tag: 'DELIVERABLES & ANALYSIS',
          title: 'Produce structured deliverables',
          desc: 'Write your analyses in a real-time collaborative editor. Ready-to-use analysis grids guide participants in structuring their reasoning.',
        },
        {
          tag: 'ECOSYSTEM MAPPING',
          title: 'Visualize networks and stakeholders',
          desc: 'Map the professional environment and relationships between stakeholders using an interactive graph tool. Identify key connections, dependencies and vulnerability points.',
        },
        {
          tag: 'DOCUMENT LIBRARY',
          title: 'Exploit your strategic documents',
          desc: 'View and analyze your reference documents directly from the platform. Field reports, intelligence notes, context documents: every piece is accessible by the entire crisis cell.',
        },
        {
          tag: 'ARTIFICIAL INTELLIGENCE',
          title: 'Augment your analysis capabilities with AI',
          desc: 'An AI assistant queries all your documents and communications to help you find key information, summarize exchanges and accelerate decision-making.',
        },
      ],
    },
    about: {
      overline: 'ABOUT',
      title: 'Train to better anticipate',
      intro: 'Anticiper was born from a simple observation: organizations that manage crises best are those that prepared for them. Our platform provides professional simulation tools so every team can train in near-real conditions.',
      methodology: {
        title: 'Our methodology',
        desc: 'Our approach rests on three fundamental pillars, drawn from best practices in operational training and crisis management.',
        pillars: [
          { title: 'Immersion', desc: 'Realistic and evolving scenarios that place participants in pressured decision-making situations.' },
          { title: 'Collaboration', desc: 'A shared work environment that reproduces the dynamics of a real crisis cell.' },
          { title: 'Analysis', desc: 'Structured tools to develop critical thinking and weak signal detection capabilities.' },
        ],
      },
      expertise: {
        title: 'Our expertise',
        desc: 'The Anticiper team brings together defense, economic intelligence and professional training specialists. Our field experience ensures relevant and immediately operational exercises.',
      },
    },
    contact: {
      overline: 'CONTACT',
      title: 'Request information',
      subtitle: 'Want to learn more about the Anticiper platform? Fill out the form below and our team will get back to you within 24 hours.',
      trust: ['Response within 24h', 'Personalized demonstration', 'No commitment'],
      form: {
        name: 'Full name',
        email: 'Professional email',
        company: 'Company',
        phone: 'Phone (optional)',
        message: 'Your message',
        messagePlaceholder: 'Describe your need in a few words...',
        submit: 'Send my request',
        success: 'Your request has been sent. We will get back to you shortly.',
      },
    },
    footer: {
      tagline: 'Crisis management training and weak signal detection platform.',
      nav: 'Navigation',
      legal: 'Legal notice',
      privacy: 'Privacy policy',
      copyright: '© 2026 Anticiper. All rights reserved.',
    },
  },
};

const I18nContext = createContext();

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState('fr');
  const t = translations[lang];
  const toggleLang = () => setLang(l => (l === 'fr' ? 'en' : 'fr'));
  return (
    <I18nContext.Provider value={{ lang, t, toggleLang }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
